import { useMachine, useSelector } from '@xstate/react'
import {
  requestMachine,
  RequestMachineContext,
  RequestMachineEventData,
  RequestMachineStates,
} from 'machines/requestMachine'
import { useUsersActions, useUsersService } from 'providers'
import { useCallback, useEffect, useRef, useState } from 'react'
import { AnyUserData, StatusType } from 'types'
import { arrayToQuery } from 'utils'
import { removeDuplicates } from 'utils/helpers'
import { State } from 'xstate'
import { useUsers } from './useUsers'
import { ListTypes } from 'pages/Dashboard'

export type ListOptions = {
  sort: string
  filters?: FilterType
  page: number
  size: number
  order: string
  teacherId: string
  type?: 'tmatt' | 'student' | 'teachers' | 'users'
}
export type FilterType = {
  type?: StatusType[]
  filters?: StatusType[]
  progress?: StatusType[]
}
export type ListParams = {
  sort: string
  filters?: FilterType
  page: number
  size: number
  order: string
  teacherId?: string
  type?: ListTypes | string
}
export type ListData = {
  users: Partial<AnyUserData>[]
  total: number
}

export type UseListOutput = {
  data: ListData
  params: ListParams
  list: (params?: Partial<ListParams>) => void
  state: State<
    RequestMachineContext,
    RequestMachineEventData,
    RequestMachineStates
  >
}

export const useList = (
  initialListOptions?: Partial<ListParams>
): UseListOutput => {
  const [state, send] = useMachine(requestMachine)
  const { list: listUsers } = useUsersActions()
  const usersService = useUsersService()
  const [listIds, setListIds] = useState<string[]>([])
  const [createdUserids, setCreatedUserIds] = useState<string[]>([])
  const [createdTeacherids, setCreatedTeacherIds] = useState<string[]>([])
  const users = useUsers()

  //The current list of users
  const [data, setData] = useState<ListData>({
    users: [],
    total: 0,
  })
  const initialSort =
    initialListOptions?.type === 'tmatt' ||
    initialListOptions?.type === 'refresher'
      ? 'enrollmentDate'
      : 'instructionDate'
  //The Params that yielded the current list of users (streching into the backend)
  // that the components can refernce for things like pagination
  const [params, setParams] = useState<ListParams>({
    page: 1,
    size: 7,
    sort: initialSort,
    order: 'desc',
    type: 'student',
    ...initialListOptions,
  })
  const paramsRef = useRef<ListParams>(params)
  const lastAdded = useRef<string>('')

  const createdUserId = useSelector(
    // @ts-ignore
    usersService,
    (state: any) => {
      if (
        !state.matches('creatingUser') ||
        lastAdded.current === state.context.lastUserEvent?.data?.id
      )
        return false
      if (
        params.type === 'tmatt' &&
        state.context.lastUserEvent.data.userType === 'Teacher'
      ) {
        lastAdded.current = state.context.lastUserEvent.data.id
        return { id: state.context.lastUserEvent.data.id, type: 'teacher' }
      } else if (
        params.type === 'student' &&
        (state.context.lastUserEvent.data.userType === 'StudentInPerson' ||
          state.context.lastUserEvent.data.userType === 'StudentDC')
      ) {
        lastAdded.current = state.context.lastUserEvent.data.id
        return { id: state.context.lastUserEvent.data.id, type: 'student' }
      }
      return false
    }
  )
  useEffect(() => {
    if (createdUserId && createdUserId.type === 'teacher') {
      setCreatedTeacherIds((listIds) =>
        listIds.includes(createdUserId.id)
          ? listIds
          : [createdUserId.id, ...listIds]
      )
    }
    if (createdUserId && createdUserId.type === 'student') {
      setCreatedUserIds((listIds) =>
        listIds.includes(createdUserId.id)
          ? listIds
          : [createdUserId.id, ...listIds]
      )
    }
  }, [createdUserId, setCreatedUserIds, setCreatedTeacherIds])

  //Keep the listIds list up to date
  useEffect(() => {
    //set the listId's if we finished a list load
    if (state.matches('success')) {
      const { results: list, total } = state.context.data
      if (list) {
        //initially load the list with the response data (to be updated with user data when available)
        setData({ total, users: list })
        setListIds(list.map(({ id }: { id: string }) => id))
      }
    }
  }, [state, setData, setListIds])

  //keep the list up to date with the freshest users and list
  useEffect(() => {
    const usersList = removeDuplicates([
      ...(params.type === 'tmatt' ? createdTeacherids : createdUserids),
      ...listIds,
    ])
      .filter((id) => users[id])
      .map((id) => users[id])
    if (usersList.length) {
      setData((list) => ({
        ...list,
        users: usersList,
      }))
    }
  }, [users, listIds, createdUserids, setData, createdTeacherids, params.type])

  const updateParams = useCallback(
    (newParams?: Partial<ListParams>) => {
      if (newParams?.page !== undefined && newParams.page < 1)
        newParams.page = 1
      const currentParams = { ...paramsRef.current, ...newParams }
      paramsRef.current = currentParams
      setParams(currentParams)
      return currentParams
    },
    [setParams, paramsRef]
  )
  const list = useCallback(
    (newParams?: Partial<ListParams>) => {
      const currentParams = updateParams(newParams)
      const filters = processFilters(currentParams.filters ?? {})
      const from = (currentParams.page - 1) * currentParams.size
      send('SET_PROMISE', {
        data: listUsers({
          ...currentParams,
          filters,
          from,
        }),
      })
    },
    [listUsers, send, updateParams]
  )

  return { data, list, params, state }
}

export const processFilters = (filterSettings: FilterType) => {
  const filters = [...(filterSettings.filters ?? [])]
  const progress = [...(filterSettings.progress ?? [])]
  const type = [...(filterSettings.type ?? [])]

  if (filters?.includes('completed')) {
    filters.push('incomplete')
  }
  if (filters?.includes('progress')) {
    filters.push('completed-review')
  }

  const filterString = arrayToQuery(filters)
  const progressString = arrayToQuery(progress)
  const typeString = arrayToQuery(type)

  return {
    status: filterString ? filterString : undefined,
    progress: progressString ? progressString : undefined,
    userType: typeString ? typeString : undefined,
  }
}
